import React from 'react';

import { Chip, Stack, Tooltip, Typography } from '@mui/material';

import { AVAILABILITY, LIST_VISIBILITY, ONLINE_PURCHASE } from '~/consts/schedule';

import { formatDateSlashesWithClock } from '~/services/TimeService';

import { getScheduleTypeLabel } from './campaignScheduleHistoryDisplay';
import type { BrandSchedule, CampaignScheduleDateEntry } from './types';

// lux_plus is shown bare (its row's Type chip already identifies it); every other type present gets
// a labeled, tooltipped pill above its date range.
const PILL_CONFIG: Partial<
  Record<BrandSchedule['type'], { color: 'warning' | 'default' | 'primary'; tooltip: string }>
> = {
  [AVAILABILITY]: { color: 'default', tooltip: 'Staff-only purchasing; BNBL date cutoff' },
  [ONLINE_PURCHASE]: { color: 'warning', tooltip: 'Purchasable via direct link (not in search)' },
  [LIST_VISIBILITY]: { color: 'primary', tooltip: 'Visible in search, carousels, and homepage' },
};

function sameInstant(a: string, b: string): boolean {
  return new Date(a).getTime() === new Date(b).getTime();
}

interface Props {
  entry: CampaignScheduleDateEntry;
  sharedWindow: { start: string | undefined; end: string | undefined } | undefined;
}

export default function CampaignScheduleDateEntryPill({ entry, sharedWindow }: Props) {
  const pillConfig = PILL_CONFIG[entry.type];
  const matchesSharedWindow =
    !!sharedWindow?.start &&
    !!sharedWindow?.end &&
    sameInstant(entry.start, sharedWindow.start) &&
    sameInstant(entry.end, sharedWindow.end);

  if (sharedWindow && matchesSharedWindow) {
    return (
      <Stack alignItems="center" direction="row" spacing={1}>
        {pillConfig && (
          <Tooltip arrow title={pillConfig.tooltip}>
            <Chip color={pillConfig.color} label={getScheduleTypeLabel(entry.type)} size="small" variant="outlined" />
          </Tooltip>
        )}
        <Typography color="text.disabled" variant="caption">
          consistent
        </Typography>
      </Stack>
    );
  }

  const isOverride = !!sharedWindow && !matchesSharedWindow;

  return (
    <Stack alignItems="center" direction="row" spacing={1}>
      {pillConfig && (
        <Tooltip arrow title={isOverride ? `Overrides the offer-wide shared schedule` : pillConfig.tooltip}>
          <Chip
            color={isOverride ? 'warning' : pillConfig.color}
            label={getScheduleTypeLabel(entry.type)}
            size="small"
            variant={isOverride ? 'filled' : 'outlined'}
          />
        </Tooltip>
      )}
      <Typography color={isOverride ? 'warning.main' : undefined} variant="body2">
        {formatDateSlashesWithClock(entry.start)} → {formatDateSlashesWithClock(entry.end)}
      </Typography>
    </Stack>
  );
}
